import { defu } from 'defu'
// Locales are imported statically (~1KB each minified): the previous dynamic
// import() was awaited in setup, and with order -100 plus gtm's dependsOn
// that one chunk fetch+parse gated the entire plugin chain at boot.
import da from 'dayjs/locale/da'
import en from 'dayjs/locale/en'
import { defineNuxtPlugin } from '#app'
import { createError, useCoreConfig, useDayjs, useHead } from '#imports'

const LOCALES = { da, en }

export default defineNuxtPlugin({
    name: 'dayjs',
    order: -100,
    setup() {
        const { dayjs: config } = useCoreConfig()

        if (!config?.locale?.name) {
            throw createError(new Error('core.dayjs.locale must be set!'))
        }

        if (!(config.locale.name in LOCALES)) {
            throw createError(new Error(`${config.locale.name} is not a valid locale. Please select one of: ${Object.keys(LOCALES).join(', ')}`))
        }

        const dayjs = useDayjs()

        dayjs.tz.setDefault(config?.defaultTimezone)

        if (import.meta.server && config.defaultTimezone) {
            // The browser loads its time-zone data on the first time-zone format, which is slow.
            // Doing that while the page parses keeps the cost out of the hydration task.
            useHead({
                script: [{
                    innerHTML: `new Intl.DateTimeFormat('en-US',{timeZone:${JSON.stringify(config.defaultTimezone)}}).format()`,
                    tagPosition: 'bodyClose',
                }],
            })
        }

        const locale = LOCALES[config.locale.name as keyof typeof LOCALES]

        // Register the locale (merged with our custom relativeTime/calendar overrides)
        // directly on THIS dayjs instance and switch to it. Relying on the locale module's
        // side-effect registration + updateLocale left the SERVER on 'en' under SSR: the
        // imported locale registers on a different dayjs copy than #dayjs, so
        // dayjs.locale(name) was a no-op there and the blog rendered English on the server
        // (then re-rendered Danish on the client → hydration mismatch). Passing the locale
        // object to dayjs.locale() registers it on #dayjs itself, so SSR uses it too.
        // Cast: runtime config holds the serialized options shape, not the ILocale type.
        dayjs.locale(config.locale.name, defu(config.locale, locale) as unknown as Partial<ILocale>)
    }
})
